import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { ClientService } from 'src/app/clients-module/services/client.service';
import { PropertyService } from 'src/app/property-module/services/property.service';
import { IntegrationProvider, PmsProperty, PmsPropertyMapping } from '../../models/integrationModels';
import { RMO_Property, RMO_PropertyMapping } from '../../models/rentManager';
import { IntegrationsService } from '../../services/integrations.service';
import { tap } from 'rxjs/operators';
import { YardiPropertyConfiguration, YardiPropertyMapping } from '../../models/yardi';

@Component({
  selector: 'app-yardi-property-mapping-modal',
  templateUrl: './yardi-property-mapping-modal.component.html',
  styleUrls: ['./yardi-property-mapping-modal.component.less']
})
export class YardiPropertyMappingModalComponent implements OnInit, OnDestroy {

  @Input() pmsIdentifier: string;
  @Input() riqClientId: number;
  @Input() pmsProvider: IntegrationProvider;

  yardiProperties: YardiPropertyConfiguration[] = [];
  yardiPropertyMappings: YardiPropertyMapping[] = [];

  locationsLoaded: boolean = false;
  mappingsLoaded: boolean = false;

  validationErrors: object = {};

  $propSub: Subscription;
  $propLoadedSub: Subscription;
  $mappingsSub: Subscription;

  constructor(public modalService: NgbModal, public locations: PropertyService, public integrations: IntegrationsService, public toastr: ToastrService) { }
  ngOnDestroy(): void {
    this.$propLoadedSub.unsubscribe();
    this.$propSub.unsubscribe();
    this.$mappingsSub.unsubscribe();
    this.locations.properties = [];
  }

  ngOnInit() {
    this.locations.FetchPropertiesForClient(this.riqClientId);
    this.$propLoadedSub = this.locations.propertiesLoaded.subscribe(res => {
      this.locationsLoaded = true;
      //fetch the RMO properties after we have the RIQ locations in hand
      this.$propSub = this.integrations.GetYardiPropertiesForClient(this.riqClientId).pipe(
        tap((res: YardiPropertyConfiguration[]) => {
          _.forEach(this.locations.properties, prop => { 
            this.validationErrors[prop.id] = false;
            prop.hasExistingPMSMapping = false;
            prop.yardiPropertyAssignmentId = "";
            });

          let emptyValue = new YardiPropertyConfiguration();
          emptyValue.code = "";
          emptyValue.marketingName = "None";
          this.yardiProperties.push(emptyValue);
          this.yardiProperties.push(...res);

          //fetch mappings
          this.$mappingsSub = this.integrations.GetYardiPropertyMappings(this.riqClientId).subscribe((res: YardiPropertyMapping[]) => {
            if(res) {
              this.yardiPropertyMappings.push(...res);
              // _.forEach(res, r => {
              //   let castmap: PmsPropertyMapping = new PmsPropertyMapping();
              //   castmap.pmsClientId = r.clientId;
              //   castmap.pmsLocationId = r.locationId;
              //   castmap.riqClientLocationId = r.riqClientLocationId;
              //   this.pmsPropertyMappings.push(castmap);
              // })
              this.populateMappings();
            } else {
              this.mappingsLoaded = true;
            }
          },
          err => { this.toastr.error('Error fetching RMO Property mappings!', '', { disableTimeOut: true, closeButton: true }) },
          () => {}) 
        })
      ).subscribe();
    },
    err => { this.locationsLoaded = false; this.toastr.error('ClientLocations failed to load!', '', { closeButton: true, disableTimeOut: true }) });

  }

  populateMappings(): void {
    _.forEach(this.yardiPropertyMappings, mapping => {
      let riqloc = _.find(this.locations.properties, prop => { return prop.id === mapping.riqClientLocationId });
      if(riqloc){
        riqloc.yardiPropertyAssignmentId = mapping.locationId;
        //NOTE TO SELF: LOCK DROPDOWNS IF A MAPPING ALREADY EXISTS!
        riqloc.hasExistingPMSMapping = true;
      }
    });
    this.mappingsLoaded = true;
  }

  save(): void {
    if(this.validate()) {
      let locMappings = this.buildPropertyMappingList();
      if(this.pmsProvider == IntegrationProvider.RentManager) {
        let rmoMappings : RMO_PropertyMapping[] = [];
        _.forEach(locMappings, map => { 
          let newmapping : RMO_PropertyMapping = new RMO_PropertyMapping();
          newmapping.clientLocationId = map.riqClientLocationId;
          newmapping.rmO_PropertyId = map.pmsLocationId;
          newmapping.corpId = map.pmsClientId;
          rmoMappings.push(newmapping);
        });
        this.integrations.SaveRMOPropertyMappings(rmoMappings).subscribe(res => {
          this.toastr.success('Property mappings saved!', '');
          this.modalService.dismissAll();
        },
        err => { this.toastr.error('Error saving property mappings!', '', { disableTimeOut: true, closeButton: true }) },
        () => {  });
      }
      if(this.pmsProvider == IntegrationProvider.Yardi) {
        console.log('YARDI MAPPINGS: ', locMappings);
        let yardiMappings: YardiPropertyMapping[] = [];
        _.forEach(locMappings, map => { 
          let newmapping : YardiPropertyMapping = new YardiPropertyMapping();
          newmapping.riqClientLocationId = map.riqClientLocationId;
          newmapping.locationId = map.pmsLocationId;
          newmapping.clientId = map.pmsClientId;
          yardiMappings.push(newmapping);
        });

        this.integrations.SaveYardiPropertyMappings(yardiMappings).pipe(
          tap(_ => {
            this.toastr.success('Property mappings saved!', '')
            this.modalService.dismissAll();
          },
          err => this.toastr.error('Error saving property mappings!', '', { disableTimeOut: true, closeButton: true }),
          () => {})
        ).subscribe();
      }
    }
    else {
      this.toastr.error('One or more validation errors exist.', '');
    }

  }

  validate(): boolean {
    let passedValidation: boolean = true;

    //the primary validation rule is that a RMO location cannot be assigned to more than one RIQ client
    //check all the properties and make sure we don't have more than one assigned rmoPropertyAssignment
    _.forEach(this.locations.properties, map => {
      this.validationErrors[map.id] = false;
      //if a rmo propId is NOT assigned, or the prop has an existing mapping, don't evaluate.
      if(map.yardiPropertyAssignmentId === undefined || map.hasExistingPMSMapping === true || !map.yardiPropertyAssignmentId) return;
      let count = _.filter(this.locations.properties, m => {   
        return m.yardiPropertyAssignmentId == map.yardiPropertyAssignmentId
      });
      if(count.length > 1 && map.yardiPropertyAssignmentId) {
        this.validationErrors[map.id] = true;
        passedValidation = false;
      }
    });

    return passedValidation;
  }

  buildPropertyMappingList(): PmsPropertyMapping[] {
    let locMappings: PmsPropertyMapping[] = [];
    _.forEach(this.locations.properties, prop => {
      if(prop.hasExistingPMSMapping || !prop.yardiPropertyAssignmentId) return;
      let newmap = new PmsPropertyMapping();
      newmap.pmsClientId = this.pmsIdentifier;
      newmap.riqClientLocationId = prop.id;
      newmap.pmsLocationId = prop.yardiPropertyAssignmentId;
      if(newmap.pmsLocationId > 0 || newmap.pmsLocationId) locMappings.push(newmap); 
    });

    return locMappings;
  }

  syncPropertyList(): void {
    _.forEach(this.locations.properties, prop => {
      if(prop.hasExistingPMSMapping) return;
      let existing = _.find(this.yardiPropertyMappings, rmo => { return rmo.locationId.toLowerCase() === prop.locationName.toLowerCase() })
      if(existing) {
        prop.yardiPropertyAssignmentId = existing.locationId;
      };
    })
  }
}
